// ---------------------------------------------------------------------------
// notifyContribution — tell Morgan when someone contributes.
//
// Why this exists (July 31): people were leaving tips and tagging roads and no
// email was arriving, because nothing was ever wired to send one.
//
//   • tip_pins       — no notification existed at all
//   • community_tags — inserted and logged, nothing more
//   • route_feedback — same
//   • notify-rating  — the edge function was written, deployed, and then
//                      referenced by absolutely nothing. Its own file was the
//                      only mention of it in the repository. It was designed
//                      to be called by a database trigger that was never
//                      created.
//
// Only road nominations were wired, which is why those were the only emails
// that ever had a chance of arriving.
//
// This is deliberately FIRE-AND-FORGET. A contribution is the user's; a
// notification is Morgan's convenience. The write has already succeeded by the
// time we get here, so nothing in this file may throw, block, or surface an
// error to someone who just did something generous.
// ---------------------------------------------------------------------------
import { supabase } from "@/integrations/supabase/client";

export type ContributionKind = "tip" | "tag" | "feedback" | "photo";

export interface ContributionNotice {
  kind: ContributionKind;
  /** Street or place name where known. */
  label?: string;
  /** The tip's prose, or the feedback text. */
  body?: string;
  /** "positive" | "negative" for tags. */
  sentiment?: string;
  /** 0..3 in-drive scale: 3 Gorgeous, 2 Nice, 1 Not nice, 0 Avoid. */
  pleasantness?: number | null;
  travelMode?: string;
  source?: string;
  /** The resolved road's name, when there is one. */
  roadName?: string;
  /** `pl:` identity of the confirmed/resolved segment. */
  segmentRef?: string;
  /** The segment's representative point — the same coordinate the public map
   *  shows. Safe to send: it is road geometry, not the contributor's fix. */
  representativeLat?: number;
  representativeLng?: number;
  userId?: string | null;
}

export function notifyContribution(notice: ContributionNotice): void {
  // Never await. Never let a mail failure reach the contributor.
  void (async () => {
    try {
      await supabase.functions.invoke("notify-rating", {
        body: {
          kind: notice.kind,
          tag_label: notice.label ?? "",
          body: notice.body ?? "",
          sentiment: notice.sentiment ?? "",
          pleasantness: notice.pleasantness ?? null,
          travel_mode: notice.travelMode ?? "",
          source: notice.source ?? "",
          // NO RAW CONTRIBUTOR COORDINATE (R1). This used to send lat/lng
          // straight from the fix, and notify-rating turned it into a Google
          // Maps link to where the person was standing. The road's name and
          // its own representative point say more and leak nothing.
          road_name: notice.roadName ?? "",
          segment_ref: notice.segmentRef ?? "",
          representative_lat: notice.representativeLat,
          representative_lng: notice.representativeLng,
          user_id: notice.userId ?? null,
        },
      });
    } catch {
      /* the contribution landed; the email is not the user's problem */
    }
  })();
}
